import React, { useEffect, useState, useRef } from 'react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { serviceAPI } from '../services/api';
import { useI18n } from '../i18n/i18nContext';
import { getTranslatedField } from '../utils/translationUtils';
import { useUser } from '../contexts/UserContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNoIndex } from '../hooks/useNoIndex';
import { getCachedData, setCachedData } from '../utils/cache';
import { useServiceMetaStore } from '../stores/serviceMetaStore';
import { Service } from '@shared/types';
import { pushToDataLayer, clearEcommerce, formatServiceItem } from '../utils/dataLayer';
import SEO from '../components/SEO';

const BookingPage: React.FC = () => {
  const navigate = useLocalizedNavigate();
  const { t, language } = useI18n();
  useDocumentTitle(t.pageTitles.book);
  useNoIndex();
  const { isLoadingUser } = useUser();
  const { setMeta } = useServiceMetaStore();
  const [services, setServices] = useState<Service[]>(() => getCachedData<Service[]>('services') || []);
  const [loading, setLoading] = useState(() => !getCachedData<Service[]>('services'));
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);
  const hasTrackedViewItemList = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (services.length > 0 && !loading && !hasTrackedViewItemList.current) {
      hasTrackedViewItemList.current = true;
      clearEcommerce();
      pushToDataLayer('view_item_list', {
        content: { category: 'booking_service' },
        ecommerce: {
          items: services.map((service) => formatServiceItem(service)),
        },
      });
    }
  }, [services, loading]);

  const fetchServices = async () => {
    const cachedServices = getCachedData<Service[]>('services');

    try {
      const response = await serviceAPI.getAll();
      const activeServices = response.data.filter((service: any) =>
        service.metadata?.is_active === true
      );

      // Store metadata for each service
      activeServices.forEach((service: any) => {
        if (service.maxParticipants) {
          setMeta(service.id, { maxParticipants: service.maxParticipants });
        }
      });

      if (JSON.stringify(activeServices) !== JSON.stringify(cachedServices)) {
        setServices(activeServices);
        setCachedData('services', activeServices);
      }
      setLoading(false);
    } catch (err) {
      console.error(t.console.errors.fetchingServices, err);
      if (!cachedServices) {
        setError(t.booking.loadError);
      }
      setLoading(false);
    }
  };

  const handleServiceSelect = (serviceId: string) => {
    navigate(`/book/${serviceId}`);
  };

  if (loading || isLoadingUser) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">{t.booking.loadingServices}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="py-10">
      <SEO description={t.seo.bookDescription} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t.booking.title}
        </h1>
        <p className="text-lg text-gray-600">
          {t.booking.step1}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {services.map((service) => (
          <div
            key={service.id}
            className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-transform hover:scale-105"
            onClick={() => handleServiceSelect(service.id)}
          >
            {service.image ? (
              <img 
                src={service.image} 
                alt={getTranslatedField(service, 'name', language)}
                className="h-48 w-full object-cover"
              />
            ) : (
              <div className="h-48 bg-gray-200 flex items-center justify-center">
                <span className="text-gray-500">
                  {getTranslatedField(service, 'name', language)}
                </span>
              </div>
            )}
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {getTranslatedField(service, 'name', language)}
              </h3>
              <p className="text-gray-600 mb-4">
                {getTranslatedField(service, 'description', language)}
              </p>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  {service.duration} {t.booking.minutes}
                </span>
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
};

export default BookingPage;